import React, { useMemo, useRef } from 'react';

import type { SerializableRecord } from '@atlassian/post-office-placement-data/types';

import type { PostOfficeMessageProps } from '../components/PostOfficeMessage';
import { PostOfficeMessage } from '../components/PostOfficeMessage';
import { transformPlacementMessageToMessageLifecycleProps } from '../util/transformPlacementMessageToMessageLifecycleProps';

import type {
	PlacementDataEndpointData,
	PlacementMessage,
	PlacementMessageDiscriminant,
	UsePlacementDataOptions,
} from './usePlacementData';
import { usePlacementData } from './usePlacementData';
import type { MatchBuilder, MatchBuilderChain, MatchBuilderInitialChain } from './utils';
import {
	PlacementLocationError,
	PlacementValidationError,
	createMatchBuilder,
	isMatchBuilder,
	isPlacementMessage,
	toHookError,
} from './utils';

/**
 * A `PostOfficeMessage` wrapper pre-bound to this placement (`placementId` is closed over).
 * `messageCategory` is closed over from the fetched message.
 * Supply `messageInstanceId` and `messageTemplateId` per-message when iterating, plus `children`.
 *
 * @example
 * ```tsx
 * const { messages, MessageComponent } = useAllMessages('my-placement');
 * return messages.map((message) => (
 *   <MessageComponent
 *     key={message.messageInstanceId}
 *     messageInstanceId={message.messageInstanceId}
 *     messageTemplateId={message.messageTemplateId}
 *   >
 *     {() => <MyUI content={message.context} />}
 *   </MessageComponent>
 * ));
 * ```
 */
export type PostOfficeAllMessages = (
	props: Omit<PostOfficeMessageProps, 'placementId' | 'messageCategory'>,
) => JSX.Element | null;

export type UseAllMessagesResult<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
> = {
	/**
	 * All non-dismissed message instances for this placement.
	 * Filtered reactively — updates immediately when `dismissMessage` is called.
	 */
	messages: Array<PlacementMessage<TMessage>>;
	/**
	 * A `PostOfficeMessage` wrapper with only `placementId` closed over.
	 * `messageCategory` is closed over from the fetched message.
	 * Pass `messageInstanceId`, `messageTemplateId`, and `children` when rendering each message.
	 */
	PostOfficeAllMessages: PostOfficeAllMessages;
	/**
	 * The set of dismissed messageInstanceIds.
	 * Exposed so callers can layer further filtering (e.g. per-template).
	 */
	dismissedIds: ReadonlySet<string>;
	/** Whether the initial Placement API fetch is in progress. */
	isLoading: boolean;
	/** Any error returned by the Placement API fetch or validation. */
	error: Error | undefined;
	/**
	 * Dismisses a message by its instance ID.
	 * Updates `messages` and `dismissedIds` immediately via pub/sub.
	 */
	dismissMessage: (messageInstanceId: string) => void;
};

export type UseAllMessagesMatchHelpers<
	TMessage extends PlacementMessageDiscriminant,
	TExtension extends SerializableRecord,
> = {
	data: PlacementDataEndpointData<TMessage, TExtension>;
	/** Fluent builder for composing message filters. */
	matchBuilder: MatchBuilderInitialChain<TMessage, TExtension>;
};

export type UseAllMessagesOptions<
	TMessage extends PlacementMessageDiscriminant,
	TExtension extends SerializableRecord,
> = {
	/**
	 * Custom function to extract and filter the message list from the raw API response.
	 * The hook always applies validation and then `dismissedIds` filtering on top of whatever this returns.
	 *
	 * Internally read via a ref, so **inline arrow functions are safe** — the latest value is always
	 * used without causing the internal `useMemo` to re-run on every render.
	 */
	match?: (
		args: UseAllMessagesMatchHelpers<TMessage, TExtension>,
	) =>
		| Array<PlacementMessage<TMessage>>
		| MatchBuilderChain<TMessage, TExtension>
		| MatchBuilderInitialChain<TMessage, TExtension>;
	/**
	 * Optional custom validator. Defaults to structural `isPlacementMessage` check when omitted.
	 *
	 * Internally read via a ref, so **inline arrow functions are safe** — the latest value is always
	 * used without causing the internal `useMemo` to re-run on every render.
	 */
	validate?: (message: unknown) => message is PlacementMessage<TMessage>;
} & UsePlacementDataOptions;

/**
 * Returns all non-dismissed message instances for a placement.
 *
 * Reads `data.messages` by default. Provide `options.match` for non-standard keys or custom filtering:
 *
 * ```tsx
 * const { messages } = useAllMessages('some-placement', {
 *   match: ({ data, matchBuilder }) =>
 *     matchBuilder.messageLocation('triggeredMessages').messageContext((ctx) => ctx.someFlag === true),
 * });
 * ```
 */
export const useAllMessages = <
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
>(
	placementId = 'headless',
	options?: UseAllMessagesOptions<TMessage, TExtension>,
): UseAllMessagesResult<TMessage> => {
	const { match, validate, dataSource } = options ?? {};

	// Store match/validate in refs so inline arrow functions passed by callers don't cause
	// the useMemo to re-run on every render. The latest value is always read inside the memo.
	const matchRef = useRef(match);
	matchRef.current = match;
	const validateRef = useRef(validate);
	validateRef.current = validate;

	const {
		data,
		dismissedIds,
		isLoading,
		error: fetchError,
		dismissMessage,
	} = usePlacementData<TMessage, TExtension>(placementId, { dataSource });

	const { candidates, validationError } = useMemo(() => {
		if (!data) {
			return { candidates: [], validationError: undefined };
		}

		let candidates: Array<PlacementMessage<TMessage>>;
		try {
			const result = matchRef.current
				? matchRef.current({ data, matchBuilder: createMatchBuilder<TMessage, TExtension>() })
				: (data.messages ?? []);

			// This allows short hand matching
			candidates = isMatchBuilder(result)
				? (result as MatchBuilder<TMessage, TExtension>).match(data)
				: (result as Array<PlacementMessage<TMessage>>);
		} catch (err) {
			if (err instanceof PlacementLocationError) {
				return { candidates: [], validationError: err };
			}
			return { candidates: [], validationError: toHookError(err) };
		}

		// Validate ALL candidates first — deterministic: invalid messages always surface an error
		// regardless of dismissed state. Dismissed filtering is applied after.
		const guard = validateRef.current ?? isPlacementMessage;
		let failedCount: number;
		try {
			failedCount = candidates.filter((m) => !guard(m)).length;
		} catch (err) {
			return { candidates: [], validationError: toHookError(err) };
		}
		if (failedCount > 0) {
			return {
				candidates: [],
				validationError: new PlacementValidationError({ placementId, failedCount }),
			};
		}

		return { candidates, validationError: undefined };
	}, [data, placementId]);

	const messages = useMemo(() => {
		return candidates.filter((m) => !dismissedIds.has(m.messageInstanceId));
	}, [candidates, dismissedIds]);

	// Build lookup from validated candidates (not dismissed-filtered messages)
	// so dismissals and data refreshes don't change PostOfficeAllMessages identity and remount callers.
	const messageByMessageInstanceId = useMemo<Record<string, PlacementMessage<TMessage>>>(() => {
		const lookup: Record<string, PlacementMessage<TMessage>> = {};

		for (const message of candidates) {
			lookup[message.messageInstanceId] = message;
		}

		return lookup;
	}, [candidates]);
	const messageByMessageInstanceIdRef = useRef<Record<string, PlacementMessage<TMessage>>>({});
	messageByMessageInstanceIdRef.current = messageByMessageInstanceId;

	// Single wrapper closed over placementId only.
	// messageInstanceId and messageTemplateId are passed per-render by the caller.
	const PostOfficeAllMessages = useMemo<PostOfficeAllMessages>(() => {
		return (props: Omit<PostOfficeMessageProps, 'placementId' | 'messageCategory'>) => {
			const message = messageByMessageInstanceIdRef.current[props.messageInstanceId];
			const inferredMessageCategory = message?.messageCategory;

			return (
				<PostOfficeMessage
					placementId={placementId}
					messageCategory={inferredMessageCategory}
					{...transformPlacementMessageToMessageLifecycleProps({ message })}
					{...props}
				/>
			);
		};
	}, [placementId]);

	return {
		messages,
		PostOfficeAllMessages,
		dismissedIds,
		isLoading,
		error: fetchError ?? validationError,
		dismissMessage,
	};
};
